import {
    isPublicationCardItem,
    Product,
    PublicationCardItemWithVideo,
    VideoQueueItemMeta,
} from '@news-mono/web-common'
import React, { useRef } from 'react'
import SwiperClass from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react.js'
import { mapListVideo } from '../../../../../libs/web-common/src/__api-mapping/v4/list-video'
import { BauhausVideoHubCard } from '../../cards/BauhausVideoCard/BauhausVideoCard'
import { invertMaybeLoadedItems } from '../../collections/helpers/loading'
import { ThemeOverrider } from '../../themes/ThemeOverrider/ThemeOverrider'
import { useProduct } from '../../__product'
import { getBauhausThemeOverride } from '../../__styling/get-bauhaus-theme-override'
import {
    StyledCarousel,
    StyledCarouselWrapper,
    StyledCarouselWrapperMobile,
} from './Carousel.styled'

export interface CarouselProps {
    onEvent: () => void
    currentVideo: PublicationCardItemWithVideo
    currentPath: string
    onSelect: (item: VideoQueueItemMeta) => void
    itemMeta: VideoQueueItemMeta[]
}
export const Carousel: React.FC<CarouselProps> = ({
    onEvent,
    currentVideo,
    onSelect,
    itemMeta,
}) => {
    const carouselWrapperVerticalRef = useRef<HTMLDivElement | null>(null)
    const swiperRef = useRef<SwiperClass>()
    if (!itemMeta || itemMeta.length === 0) {
        return null
    }
    const items = invertMaybeLoadedItems(
        {
            loaded: true,
            result: itemMeta.map((meta) =>
                mapListVideo(meta, {
                    publicUrl: '',
                }),
            ),
        },
        11,
    )
    // onclick scroll card to top in vertical carousel
    const handleCarouselSelection = (offsetTop?: number) => {
        if (carouselWrapperVerticalRef.current) {
            if (offsetTop) {
                carouselWrapperVerticalRef.current.scrollTop = offsetTop - 3
            }
        }
    }
    const handleSwipperSelection = (itemNo?: number) => {
        if (swiperRef.current) {
            if (itemNo) {
                swiperRef.current.slideTo(itemNo)
            }
        }
    }

    if (items[0].loaded && isPublicationCardItem(items[0].result))
        return (
            <>
                <CarouselComponent>
                    <StyledCarousel
                        ref={carouselWrapperVerticalRef}
                        // fix for lazy loading images in a scrollable container
                        onScroll={() => {
                            if (
                                typeof window === 'undefined' ||
                                typeof document === 'undefined'
                            ) {
                                return
                            }
                            window.dispatchEvent(new CustomEvent('scroll'))
                        }}
                    >
                        {items.map((item, idx) => {
                            if (
                                item.loaded &&
                                isPublicationCardItem(item.result) &&
                                currentVideo
                            ) {
                                const cardThemeOverride =
                                    getBauhausThemeOverride(
                                        item.result.primaryTopic.id,
                                    )
                                const matchingMeta = itemMeta.find(
                                    (el) => item.result.id === el.id,
                                )
                                if (matchingMeta) {
                                    return (
                                        <ThemeOverrider
                                            override={cardThemeOverride}
                                            key={idx}
                                        >
                                            <BauhausVideoHubCard
                                                key={idx}
                                                item={item}
                                                onEvent={onEvent}
                                                isCurrentlyPlaying={
                                                    item.result.id ===
                                                    currentVideo.id
                                                }
                                                handleCarouselSelection={
                                                    handleCarouselSelection
                                                }
                                                handleSwipperSelection={() =>
                                                    handleSwipperSelection(idx)
                                                }
                                                onSelect={onSelect}
                                                itemMeta={matchingMeta}
                                            />
                                        </ThemeOverrider>
                                    )
                                }
                            }
                        })}
                    </StyledCarousel>
                </CarouselComponent>

                <StyledCarouselWrapperMobile>
                    <Swiper
                        slidesPerView={'auto'}
                        slidesPerGroup={1}
                        spaceBetween={15}
                        loop={false}
                        watchOverflow={true}
                        slideToClickedSlide={true}
                        onBeforeInit={(swiper) => {
                            swiperRef.current = swiper
                        }}
                    >
                        {items.map((item, idx) => {
                            if (
                                item.loaded &&
                                isPublicationCardItem(item.result)
                            ) {
                                const cardThemeOverride =
                                    getBauhausThemeOverride(
                                        item.result.primaryTopic.id,
                                    )
                                const matchingMeta = itemMeta.find(
                                    (el) => item.result.id === el.id,
                                )
                                if (matchingMeta) {
                                    return (
                                        <SwiperSlide
                                            key={idx}
                                            virtualIndex={idx}
                                        >
                                            <ThemeOverrider
                                                override={cardThemeOverride}
                                            >
                                                <BauhausVideoHubCard
                                                    item={item}
                                                    onEvent={onEvent}
                                                    isCurrentlyPlaying={
                                                        item.result.id ===
                                                        currentVideo.id
                                                    }
                                                    handleCarouselSelection={
                                                        handleCarouselSelection
                                                    }
                                                    handleSwipperSelection={() =>
                                                        handleSwipperSelection(
                                                            idx,
                                                        )
                                                    }
                                                    onSelect={onSelect}
                                                    itemMeta={matchingMeta}
                                                />
                                            </ThemeOverrider>
                                        </SwiperSlide>
                                    )
                                }
                            }
                        })}
                    </Swiper>
                </StyledCarouselWrapperMobile>
            </>
        )
    return null
}

const CarouselComponent: React.FC = ({ children }) => {
    const product = useProduct()
    if (product === Product.SevenNews) {
        // need to set Carousel in absolute positioned div, to prevent setting height of hub component based on height of carousel
        return <StyledCarouselWrapper>{children}</StyledCarouselWrapper>
    }
    return <>{children}</>
}
